import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { parseUntrackedCsv, parseUntrackedTouchpointCsv, UNTRACKED_CSV_COLUMNS, TOUCHPOINT_CSV_COLUMNS, type ParsedCsvResult } from '../utils/csvUpload';
import { uploadUntrackedCsv, fetchUntrackedData, uploadUntrackedTouchpointCsv, fetchUntrackedTouchpointData, type UploadUntrackedResult, type UploadUntrackedMode } from '../utils/database';
import { transformRequestResponse, transformTouchpointResponse } from '../utils/transformers';
interface Props {
  onClose: () => void;
  onUploaded: () => void;
}
type UploadTarget = 'requests' | 'touchpoints';
const PREVIEW_ROW_CAP = 200;
interface ModeConfig {
  title: string;
  columns: readonly string[];
  filenamePrefix: string;
  parse: (file: File) => Promise<ParsedCsvResult>;
  upload: (rows: Record<string, string>[], mode: UploadUntrackedMode) => Promise<UploadUntrackedResult>;
  fetchExport: () => Promise<Record<string, string>[]>;
  transformForDownload: (rows: Record<string, string>[]) => unknown[];
}
const MODE_CONFIG: Record<UploadTarget, ModeConfig> = {
  requests: {
    title: 'Upload untracked requests (CSV)',
    columns: UNTRACKED_CSV_COLUMNS,
    filenamePrefix: 'wf_untracked_requests',
    parse: parseUntrackedCsv,
    upload: uploadUntrackedCsv,
    fetchExport: fetchUntrackedData,
    transformForDownload: transformRequestResponse
  },
  touchpoints: {
    title: 'Upload untracked touchpoints (CSV)',
    columns: TOUCHPOINT_CSV_COLUMNS,
    filenamePrefix: 'wf_untracked_touchpoints',
    parse: parseUntrackedTouchpointCsv,
    upload: uploadUntrackedTouchpointCsv,
    fetchExport: fetchUntrackedTouchpointData,
    transformForDownload: transformTouchpointResponse
  }
};
export function UploadCsvModal({
  onClose,
  onUploaded
}: Props) {
  const [target, setTarget] = useState<UploadTarget>('requests');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedCsvResult | null>(null);
  const [mode, setMode] = useState<UploadUntrackedMode>('append');
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadUntrackedResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cfg = MODE_CONFIG[target];
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  const invalidCount = parsed ? parsed.rows.filter(r => r.issues.length > 0).length : 0;
  function resetState() {
    setFileName(null);
    setParsed(null);
    setMode('append');
    setUploadResult(null);
    setUploadError(null);
    setDownloadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }
  function handleTargetChange(next: UploadTarget) {
    if (next === target) return;
    resetState();
    setTarget(next);
  }
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploadResult(null);
    setUploadError(null);
    setIsParsing(true);
    try {
      const result = await cfg.parse(file);
      setParsed(result);
    } finally {
      setIsParsing(false);
    }
  }
  function handleClear() {
    resetState();
  }
  async function handleUpload() {
    if (!parsed) return;
    if (mode === 'replace') {
      const ok = window.confirm('Replace mode deletes ALL existing untracked rows before inserting this upload. This cannot be undone. Continue?');
      if (!ok) return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      const rows = parsed.rows.map(r => r.raw);
      const result = await cfg.upload(rows, mode);
      setUploadResult(result);
      onUploaded();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }
  async function handleDownload() {
    setDownloadError(null);
    setIsDownloading(true);
    try {
      const rows = await cfg.fetchExport();
      const csv = Papa.unparse(cfg.transformForDownload(rows), {
        columns: [...cfg.columns]
      });
      const blob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cfg.filenamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  }
  const previewRows = parsed ? parsed.rows.slice(0, PREVIEW_ROW_CAP) : [];
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-4 px-2" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-1221" data-mp="38k6jbdsmjhpx">
      <div className="relative bg-white rounded-xl shadow-2xl w-full" style={{
      maxWidth: '98vw'
    }} onClick={e => e.stopPropagation()} data-id="element-1222" data-mp="51vf8zbir3lm">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200" data-id="element-1223" data-mp="3r9d73f89yzil">
          <div data-id="element-1224" data-mp="31z0z4ukyhr">
            <div className="flex items-center gap-1 mb-2" data-id="element-1225" data-mp="2salfzgaze6">
              <button onClick={() => handleTargetChange('requests')} className={`px-3 py-1 text-xs font-medium rounded-md border ${target === 'requests' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} data-id="element-1226" data-mp="17g1qd4zggb">
                Requests
              </button>
              <button onClick={() => handleTargetChange('touchpoints')} className={`px-3 py-1 text-xs font-medium rounded-md border ${target === 'touchpoints' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} data-id="element-1227" data-mp="hkh6eyw1hj6e">
                Touchpoints
              </button>
            </div>
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-800" data-id="element-1228" data-mp="3utdan58gv9vq">{cfg.title}</h2>
            <p className="mt-1 text-xs text-slate-500" data-id="element-1229" data-mp="3puai2k9oaw4n">
              Columns expected: {cfg.columns.join(', ')}. Dates must be dd/MM/yy.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-base leading-none font-bold" aria-label="Close" data-id="element-1230" data-mp="1o24cnbzcauyw">
            ✕
          </button>
        </div>

        <div className="px-6 py-4" data-id="element-1231" data-mp="20hc5ceebhlhi">
          <div className="flex items-center justify-between mb-3" data-id="element-1232" data-mp="259lu3r3p4fi7">
            <button onClick={() => void handleDownload()} disabled={isDownloading} className="px-3 py-1 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors" data-id="element-1233" data-mp="25chs1ffjn46n">
              {isDownloading ? 'Downloading…' : 'Download current data (CSV)'}
            </button>
          </div>
          {downloadError && <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-id="element-1234" data-mp="1rja17pzaecq0">
              {downloadError}
            </div>}

          {!parsed && <div className="flex items-center gap-3" data-id="element-1235" data-mp="32xe1fdwgwx3i">
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={e => void handleFileChange(e)} className="text-xs" data-id="element-1236" data-mp="33009rfr6ngms" />
              {isParsing && <span className="text-xs text-slate-500" data-id="element-1237" data-mp="1mvddvei5d9v">Parsing…</span>}
            </div>}

          {parsed && <>
              <div className="flex items-center justify-between mb-3" data-id="element-1238" data-mp="3szq3y9h8vf1q">
                <div className="text-xs text-slate-600" data-id="element-1239" data-mp="am0ftct6pqrb">
                  <strong data-id="element-1240" data-mp="6lgj2aciwp5p">{fileName}</strong> — {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} parsed
                  {invalidCount > 0 && <span className="text-red-600 font-medium" data-id="element-1241" data-mp="kkpm08bpxmrq"> · {invalidCount} row{invalidCount === 1 ? '' : 's'} with issues (highlighted below)</span>}
                  {parsed.rows.length > PREVIEW_ROW_CAP && <span data-id="element-1242" data-mp="2hdhnkp350uv4"> · showing first {PREVIEW_ROW_CAP} rows</span>}
                </div>
                <div className="flex items-center gap-3" data-id="element-1243" data-mp="wvqoojurm5am">
                  <div className="flex items-center gap-3 text-xs text-slate-600" data-id="element-1244" data-mp="ww8sc1jrblsk">
                    <label className="flex items-center gap-1 cursor-pointer" data-id="element-1245" data-mp="11t8qlqad7k94">
                      <input type="radio" name="upload-mode" checked={mode === 'append'} onChange={() => setMode('append')} data-id="element-1246" data-mp="n6fesv020wlo" />
                      Append
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer" data-id="element-1247" data-mp="e4g0wbkc68my">
                      <input type="radio" name="upload-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} data-id="element-1248" data-mp="j42c6fgltet4" />
                      Replace existing
                    </label>
                  </div>
                  <button onClick={handleClear} className="px-3 py-1 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-50 transition-colors" data-id="element-1249" data-mp="95ct4jcz1oba">
                    Clear / re-upload CSV
                  </button>
                  <button onClick={() => void handleUpload()} disabled={isUploading || parsed.rows.length === 0} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-1250" data-mp="p7q7hv885gbd">
                    {isUploading ? 'Uploading…' : mode === 'replace' ? 'Replace existing data' : 'Upload to dashboard'}
                  </button>
                </div>
              </div>

              {mode === 'replace' && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-id="element-1251" data-mp="1cvrl3ik9fmxn">
                  ⚠ Replace mode will delete all previously uploaded untracked rows before inserting this file.
                </div>}

              {parsed.fileErrors.length > 0 && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-id="element-1252" data-mp="3npvtnl8v3mas">
                  {parsed.fileErrors.map((e, i) => <div key={i} data-id="element-1253" data-mp="25qpf9bhtrx3a" data-mpi={i}>{e}</div>)}
                </div>}

              {uploadError && <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-id="element-1254" data-mp="20sxq2p8kep2s">
                  {uploadError}
                </div>}

              {uploadResult && <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-id="element-1255" data-mp="2apiw9dwbm687">
                  ✓ Uploaded ({uploadResult.mode}) — {uploadResult.inserted} row{uploadResult.inserted === 1 ? '' : 's'} inserted
                  {uploadResult.skippedCount > 0 && `, ${uploadResult.skippedCount} skipped`}.
                  {uploadResult.skipped.length > 0 && <ul className="mt-1 list-disc list-inside" data-id="element-1256" data-mp="13ds5oph0c3wr">
                      {uploadResult.skipped.slice(0, 20).map((s, i) => <li key={i} data-id="element-1257" data-mp="zcggum5yjmpm" data-mpi={i}>{s}</li>)}
                    </ul>}
                </div>}

              <div style={{
            overflowX: 'auto',
            maxHeight: '55vh',
            overflowY: 'auto'
          }} className="border border-slate-200 rounded-md" data-id="element-1258" data-mp="2yfx14smlhls4">
                <table className="tbl tbl-scroll" data-id="element-1259" data-mp="33dyhh5smz6zw">
                  <thead data-id="element-1260" data-mp="386hwm76z2a6t">
                    <tr data-id="element-1261" data-mp="3879slj4he9xm">
                      {cfg.columns.map(c => <th key={c} data-id="element-1262" data-mp="3d5l21sqv6ifb">{c}</th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-1263" data-mp="2fi3xtp8279u7">
                    {previewRows.map(row => <tr key={row.index} style={row.issues.length > 0 ? {
                  background: '#fef2f2'
                } : undefined} title={row.issues.join('; ')} data-id="element-1264" data-mp="2fk6wexna2baj">
                        {cfg.columns.map(c => <td key={c} style={{
                    fontSize: 10
                  }} data-id="element-1265" data-mp="2peyu01gv0y5b">{row.raw[c] ?? ''}</td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </>}
        </div>
      </div>
    </div>;
}